import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../api/axiosManager';
import {
  resolveCampaignPersonalized,
} from '../../utils/campaignTypeDisplay';

// Tarih dönüştürücü - .NET formatından dd.mm.yyyy'ye
const convertDateFromAPI = (dateString, fieldName = 'unknown') => {
  if (!dateString) {
    return '-';
  }
  
  try {
    let date;
    const stringDate = String(dateString);
    
    // 1. Önce .NET formatını kontrol et: /Date(1760426549750)/
    const dotNetMatch = stringDate.match(/\/Date\((\d+)\)\//);
    if (dotNetMatch) {
      const timestamp = parseInt(dotNetMatch[1]);
      date = new Date(timestamp);
    } 
    // 2. ISO string formatını kontrol et: 2026-02-10T00:00:00.000Z
    else if (stringDate.includes('T') || stringDate.includes('-')) {
      date = new Date(stringDate);
    }
    // 3. Direkt timestamp (number veya string olarak)
    else {
      const numMatch = stringDate.match(/\d+/)?.[0];
      if (numMatch) {
        const timestamp = parseInt(numMatch);
        date = new Date(timestamp);
      } else {
        return '-';
      }
    }
    
    // Geçerli bir tarih mi kontrol et
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const result = `${day}.${month}.${year}`;
    
    return result;
  } catch (error) {
    return '-';
  }
};

// Kişisel Kampanyalar
export const fetchPersonalCampaigns = createAsyncThunk(
  'campaigns/fetchPersonalCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/dashboard/campaigns/personal');
      
      if (response.data && response.data.success && response.data.data) {
        const mappedCampaigns = response.data.data.map((campaign, index) => {
          
          return {
            // API'den gelen alanlar
            id: campaign.attributeCode,
            attributeCode: campaign.attributeCode,
            discountOfferCode: campaign.discountOfferCode,
            discountOfferDescription: campaign.discountOfferDescription,
            campaignImage: campaign.campaignImage || campaign.CampaignImage || '',
            campaignImage2: campaign.campaignImage2 || campaign.CampaignImage2 || '', // ✅ Kiosk görseli (her iki formatı da kontrol et)
            campaignTitle: campaign.campaignTitle || campaign.CampaignTitle || '',
            campaignContent: campaign.campaignContent || campaign.CampaignContent || '',
            campaignContentShort: campaign.campaignContentShort || campaign.CampaignContentShort || '',
            campaignFeatures: campaign.campaignFeatures || campaign.CampaignFeatures || '',
            campaignTermsAndConditions: campaign.campaignTermsAndConditions || campaign.CampaignTermsAndConditions || '',
            priority: campaign.priority ?? campaign.Priority ?? campaign.campaignPriority ?? '',
            offices: campaign.offices || campaign.Offices || '',
            zamanPeriyodu: campaign.zamanPeriyodu || campaign.ZamanPeriyodu || '',
            isActiveApp: campaign.isActiveApp !== undefined ? campaign.isActiveApp : (campaign.IsActiveApp !== undefined ? campaign.IsActiveApp : 0),
            overwriteStartDate: convertDateFromAPI(campaign.overwriteStartDate || campaign.OverwriteStartDate, `personal[${index}].overwriteStartDate`),
            overwriteEndDate: convertDateFromAPI(campaign.overwriteEndDate || campaign.OverwriteEndDate, `personal[${index}].overwriteEndDate`),
            hideShowEndDate: campaign.hideShowEndDate ?? campaign.HideShowEndDate ?? 0,
            // Görüntüleme için düzenlenmiş alanlar
            title: campaign.campaignTitle || campaign.discountOfferDescription || '-',
            image: campaign.campaignImage || '',
            startDate: convertDateFromAPI(campaign.startDate || campaign.StartDate, `personal[${index}].startDate`),
            endDate: convertDateFromAPI(campaign.endDate || campaign.EndDate, `personal[${index}].endDate`),
            status: campaign.status || (campaign.isActiveApp === 1 || campaign.IsActiveApp === 1 ? 'active' : 'inactive'),
            participation: campaign.participationRate || 0,
            revenue: campaign.revenue || 0,
          };
        });
        
        return mappedCampaigns;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Kişisel kampanyalar yüklenemedi');
    }
  }
);

// Referans Çekleri
export const fetchReferralCampaigns = createAsyncThunk(
  'campaigns/fetchReferralCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/dashboard/campaigns/referral');
      
      if (response.data && response.data.success && response.data.data) {
        const mappedCampaigns = response.data.data.map(campaign => ({
          // API'den gelen alanlar
          id: campaign.DiscountVoucherTypeCode,
          voucherCode: campaign.DiscountVoucherTypeCode,
          description: campaign.DiscountVoucherTypeDescription || '',
          campaignImage: campaign.CampaignImage || '',
          campaignTitle: campaign.CampaignTitle || '',
          campaignContent: campaign.CampaignContent || '',
          campaignContentShort: campaign.CampaignContentShort || '',
          campaignFeatures: campaign.CampaignFeatures || '',
          campaignTermsAndConditions: campaign.CampaignTermsAndConditions || '',
          priority: campaign.priority ?? campaign.Priority ?? campaign.campaignPriority ?? '',
          isActiveApp: campaign.isActiveApp !== undefined ? campaign.isActiveApp : (campaign.IsActiveApp !== undefined ? campaign.IsActiveApp : 0),
          overwriteStartDate: convertDateFromAPI(campaign.OverwriteStartDate),
          overwriteEndDate: convertDateFromAPI(campaign.OverwriteEndDate),
          hideShowEndDate: campaign.HideShowEndDate ?? 0,
          // Görüntüleme için düzenlenmiş alanlar
          title: campaign.CampaignTitle || campaign.DiscountVoucherTypeDescription || '-',
          image: campaign.CampaignImage || '',
          startDate: convertDateFromAPI(campaign.StartDate),
          endDate: convertDateFromAPI(campaign.EndDate),
          status: campaign.isActiveApp === 1 || campaign.IsActiveApp === 1 ? 'active' : 'inactive',
          participation: 0, // API'de bu alan yok
          revenue: 0, // API'de bu alan yok
        }));
        
        return mappedCampaigns;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Referans çekleri yüklenemedi');
    }
  }
);

// Hediye Çekleri
export const fetchGiftCards = createAsyncThunk(
  'campaigns/fetchGiftCards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/dashboard/gift-cards');
      
      if (response.data && response.data.success && response.data.data) {
        const mappedGiftCards = response.data.data.map(giftCard => ({
          // API'den gelen alanlar
          id: giftCard.GiftItemCode,
          giftItemCode: giftCard.GiftItemCode,
          description: giftCard.GiftItemDescription || '',
          campaignImage: giftCard.CampaignImage || '',
          campaignTitle: giftCard.CampaignTitle || '',
          campaignContent: giftCard.CampaignContent || '',
          campaignContentShort: giftCard.CampaignContentShort || '',
          campaignFeatures: giftCard.CampaignFeatures || '',
          campaignTermsAndConditions: giftCard.CampaignTermsAndConditions || '',
          priority: giftCard.priority ?? giftCard.Priority ?? giftCard.campaignPriority ?? '',
          isActiveApp: giftCard.isActiveApp !== undefined ? giftCard.isActiveApp : (giftCard.IsActiveApp !== undefined ? giftCard.IsActiveApp : 0),
          overwriteStartDate: convertDateFromAPI(giftCard.OverwriteStartDate),
          overwriteEndDate: convertDateFromAPI(giftCard.OverwriteEndDate),
          hideShowEndDate: giftCard.HideShowEndDate ?? 0,
          // Görüntüleme için düzenlenmiş alanlar
          title: giftCard.CampaignTitle || giftCard.GiftItemDescription || '-',
          image: giftCard.CampaignImage || '',
          startDate: convertDateFromAPI(giftCard.StartDate),
          endDate: convertDateFromAPI(giftCard.EndDate),
          status: giftCard.isActiveApp === 1 || giftCard.IsActiveApp === 1 ? 'active' : 'inactive',
          participation: 0, // API'de bu alan yok
          revenue: 0, // API'de bu alan yok
        }));
        
        return mappedGiftCards;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Hediye çekleri yüklenemedi');
    }
  }
);

// Tüm kampanyaların istatistiklerini getir (pagination + dateRange)
export const fetchCampaignsStats = createAsyncThunk(
  'campaigns/fetchCampaignsStats',
  async (options = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        limit = 20,
        sortBy = 'ctr',
        order = 'desc',
        dateRange,
        dateFrom,
        dateTo,
        isPersonalized,
      } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortBy,
        order,
      });

      if (dateRange) {
        params.append('dateRange', dateRange);
      } else {
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
      }

      if (isPersonalized === true || isPersonalized === false) {
        params.append('isPersonalized', String(isPersonalized));
      }

      const endpoint = `/api/admin/analytics/campaigns/stats?${params.toString()}`;
      const response = await api.get(endpoint);

      if (response.data && response.data.success) {
        const rows = (response.data.data || []).map((row) => ({
          ...row,
          isPersonalized: resolveCampaignPersonalized(row),
        }));

        return {
          data: rows,
          pagination: response.data.pagination || null,
        };
      }
      return rejectWithValue('Kampanya istatistikleri alınamadı');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Kampanya istatistikleri alınamadı');
    }
  }
);

// Genel Kampanyalar
export const fetchGeneralCampaigns = createAsyncThunk(
  'campaigns/fetchGeneralCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/dashboard/campaigns/general');
      
      // ✅ Önce direkt array kontrolü yap (backend bazen direkt array döndürebilir)
      if (Array.isArray(response.data)) {
        const mappedCampaigns = response.data.map((campaign, index) => {
          const rawIsActive = campaign.IsActiveApp ?? campaign.isActiveApp;
          const mappedIsActive = rawIsActive ?? 0;
          const mappedStatus = mappedIsActive === 1 ? 'active' : 'inactive';
          return {
            // API'den gelen alanlar
            id: campaign.DiscountOfferCode,
            discountOfferCode: campaign.DiscountOfferCode,
            discountOfferDescription: campaign.DiscountOfferDescription || '',
            campaignImage: campaign.CampaignImage || '',
            campaignImage2: campaign.CampaignImage2 || campaign.campaignImage2 || '', // ✅ Kiosk küçük banner görseli (her iki formatı da kontrol et)
            campaignImage3: campaign.CampaignImage3 || campaign.campaignImage3 || '', // ✅ Kiosk büyük banner görseli (her iki formatı da kontrol et)
            campaignTitle: campaign.CampaignTitle || '',
            campaignContent: campaign.CampaignContent || '',
            campaignContentShort: campaign.CampaignContentShort || '',
            campaignFeatures: campaign.CampaignFeatures || '',
            campaignTermsAndConditions: campaign.CampaignTermsAndConditions || '',
            priority: campaign.priority ?? campaign.Priority ?? campaign.campaignPriority ?? '',
            offices: campaign.Offices || '',
            isActiveApp: mappedIsActive,
            overwriteStartDate: convertDateFromAPI(campaign.OverwriteStartDate, `general[${index}].overwriteStartDate`),
            overwriteEndDate: convertDateFromAPI(campaign.OverwriteEndDate, `general[${index}].overwriteEndDate`),
            hideShowEndDate: campaign.HideShowEndDate ?? 0,
            // Görüntüleme için düzenlenmiş alanlar
            title: campaign.CampaignTitle || campaign.DiscountOfferDescription || '-',
            image: campaign.CampaignImage || '',
            startDate: convertDateFromAPI(campaign.StartDate, `general[${index}].startDate`),
            endDate: convertDateFromAPI(campaign.EndDate, `general[${index}].endDate`),
            status: mappedStatus,
            participation: 0, // API'de bu alan yok
            revenue: 0, // API'de bu alan yok
          };
        });
        
        return mappedCampaigns;
      }
      
      if (response.data && response.data.success && response.data.data) {
        const mappedCampaigns = response.data.data.map((campaign, index) => {
          const rawIsActive = campaign.IsActiveApp ?? campaign.isActiveApp;
          const mappedIsActive = rawIsActive ?? 0;
          const mappedStatus = mappedIsActive === 1 ? 'active' : 'inactive';
          const mapped = {
            // API'den gelen alanlar
            id: campaign.DiscountOfferCode,
            discountOfferCode: campaign.DiscountOfferCode,
            discountOfferDescription: campaign.DiscountOfferDescription || '',
            campaignImage: campaign.CampaignImage || '',
            campaignImage2: campaign.CampaignImage2 || campaign.campaignImage2 || '', // ✅ Kiosk küçük banner görseli (her iki formatı da kontrol et)
            campaignImage3: campaign.CampaignImage3 || campaign.campaignImage3 || '', // ✅ Kiosk büyük banner görseli (her iki formatı da kontrol et)
            campaignTitle: campaign.CampaignTitle || '',
            campaignContent: campaign.CampaignContent || '',
            campaignContentShort: campaign.CampaignContentShort || '',
            campaignFeatures: campaign.CampaignFeatures || '',
            campaignTermsAndConditions: campaign.CampaignTermsAndConditions || '',
            priority: campaign.priority ?? campaign.Priority ?? campaign.campaignPriority ?? '',
            offices: campaign.Offices || '',
            isActiveApp: mappedIsActive,
            overwriteStartDate: convertDateFromAPI(campaign.OverwriteStartDate, `general[${index}].overwriteStartDate`),
            overwriteEndDate: convertDateFromAPI(campaign.OverwriteEndDate, `general[${index}].overwriteEndDate`),
            hideShowEndDate: campaign.HideShowEndDate ?? 0,
            // Görüntüleme için düzenlenmiş alanlar
            title: campaign.CampaignTitle || campaign.DiscountOfferDescription || '-',
            image: campaign.CampaignImage || '',
            startDate: convertDateFromAPI(campaign.StartDate, `general[${index}].startDate`),
            endDate: convertDateFromAPI(campaign.EndDate, `general[${index}].endDate`),
            status: mappedStatus,
            participation: 0, // API'de bu alan yok
            revenue: 0, // API'de bu alan yok
          };

          return mapped;
        });
        
        return mappedCampaigns;
      } else {
      return [];
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Genel kampanyalar yüklenemedi');
    }
  }
);

// Kişisel Kampanya Güncelleme
export const updatePersonalCampaign = createAsyncThunk(
  'campaigns/updatePersonalCampaign',
  async ({ attributeCode, discountOfferCode, campaignData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/api/admin/dashboard/campaigns/personal/${attributeCode}/${discountOfferCode}`,
        campaignData
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Kampanya güncellenemedi');
    }
  }
);

// Genel Kampanya Güncelleme
export const updateGeneralCampaign = createAsyncThunk(
  'campaigns/updateGeneralCampaign',
  async ({ discountOfferCode, campaignData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/api/admin/dashboard/campaigns/general/${discountOfferCode}`,
        campaignData
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Genel kampanya güncellenemedi');
    }
  }
);

// Referans Çeki Güncelleme
export const updateReferralCampaign = createAsyncThunk(
  'campaigns/updateReferralCampaign',
  async ({ voucherCode, campaignData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/api/admin/dashboard/campaigns/referral/${voucherCode}`,
        campaignData
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Referans çeki güncellenemedi');
    }
  }
);

// Hediye Çeki Güncelleme
export const updateGiftCard = createAsyncThunk(
  'campaigns/updateGiftCard',
  async ({ giftItemCode, campaignData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/api/admin/dashboard/gift-cards/${giftItemCode}`,
        campaignData
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data || 'Hediye çeki güncellenemedi');
    }
  }
);

export const reorderGeneralCampaigns = createAsyncThunk(
  'campaigns/reorderGeneralCampaigns',
  async (items, { rejectWithValue }) => {
    try {
      const endpoint = '/api/admin/dashboard/campaigns/general/priority/bulk';
      const response = await api.put(endpoint, { items });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Genel kampanya siralama guncellenemedi');
    }
  }
);

export const reorderPersonalCampaigns = createAsyncThunk(
  'campaigns/reorderPersonalCampaigns',
  async (items, { rejectWithValue }) => {
    try {
      const endpoint = '/api/admin/dashboard/campaigns/personal/priority/bulk';
      const response = await api.put(endpoint, { items });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Bana ozel kampanya siralama guncellenemedi');
    }
  }
);

const initialState = {
  // Kişisel Kampanyalar
  personalCampaigns: [],
  personalLoading: false,
  personalError: null,
  
  // Referans Çekleri
  referralCampaigns: [],
  referralLoading: false,
  referralError: null,
  
  // Hediye Çekleri
  giftCards: [],
  giftCardsLoading: false,
  giftCardsError: null,
  
  // Genel Kampanyalar
  generalCampaigns: [],
  generalLoading: false,
  generalError: null,

  // Kampanya istatistikleri (tüm kampanyalar)
  campaignStats: [],
  campaignStatsPagination: null,
  campaignStatsLoading: false,
  campaignStatsError: null,
  
  // Genel durum
  loading: false,
  error: null,
};

const campaignSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearCampaigns: (state) => {
      state.personalCampaigns = [];
      state.referralCampaigns = [];
      state.giftCards = [];
      state.generalCampaigns = [];
      state.personalError = null;
      state.referralError = null;
      state.giftCardsError = null;
      state.generalError = null;
    },
    clearPersonalCampaigns: (state) => {
      state.personalCampaigns = [];
      state.personalError = null;
    },
    clearReferralCampaigns: (state) => {
      state.referralCampaigns = [];
      state.referralError = null;
    },
    clearGiftCards: (state) => {
      state.giftCards = [];
      state.giftCardsError = null;
    },
    clearGeneralCampaigns: (state) => {
      state.generalCampaigns = [];
      state.generalError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Kişisel Kampanyalar
      .addCase(fetchPersonalCampaigns.pending, (state) => {
        state.personalLoading = true;
        state.personalError = null;
      })
      .addCase(fetchPersonalCampaigns.fulfilled, (state, action) => {
        state.personalLoading = false;
        state.personalCampaigns = action.payload;
        state.personalError = null;
      })
      .addCase(fetchPersonalCampaigns.rejected, (state, action) => {
        state.personalLoading = false;
        state.personalError = action.payload;
      })
      
      // Referans Çekleri
      .addCase(fetchReferralCampaigns.pending, (state) => {
        state.referralLoading = true;
        state.referralError = null;
      })
      .addCase(fetchReferralCampaigns.fulfilled, (state, action) => {
        state.referralLoading = false;
        state.referralCampaigns = action.payload;
        state.referralError = null;
      })
      .addCase(fetchReferralCampaigns.rejected, (state, action) => {
        state.referralLoading = false;
        state.referralError = action.payload;
      })
      
      // Hediye Çekleri
      .addCase(fetchGiftCards.pending, (state) => {
        state.giftCardsLoading = true;
        state.giftCardsError = null;
      })
      .addCase(fetchGiftCards.fulfilled, (state, action) => {
        state.giftCardsLoading = false;
        state.giftCards = action.payload;
        state.giftCardsError = null;
      })
      .addCase(fetchGiftCards.rejected, (state, action) => {
        state.giftCardsLoading = false;
        state.giftCardsError = action.payload;
      })
      
      // Genel Kampanyalar
      .addCase(fetchGeneralCampaigns.pending, (state) => {
        state.generalLoading = true;
        state.generalError = null;
      })
      .addCase(fetchGeneralCampaigns.fulfilled, (state, action) => {
        state.generalLoading = false;
        state.generalCampaigns = action.payload;
        state.generalError = null;
      })
      .addCase(fetchGeneralCampaigns.rejected, (state, action) => {
        state.generalLoading = false;
        state.generalError = action.payload;
      })
      
      // Kişisel Kampanya Güncelleme
      .addCase(updatePersonalCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePersonalCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // ✅ PUT response'undan gelen veriyi map et ve state'e ekle
        const response = action.payload;
        let updatedCampaignData = null;
        
        // Response formatı: { success: true, data: { originalResponse: {...}, ... } }
        if (response?.data?.originalResponse) {
          // originalResponse'dan gelen ham veriyi map et
          const rawCampaign = response.data.originalResponse;
          updatedCampaignData = {
            id: rawCampaign.AttributeCode || rawCampaign.attributeCode || response.data.attributeCode,
            attributeCode: rawCampaign.AttributeCode || rawCampaign.attributeCode || response.data.attributeCode,
            discountOfferCode: rawCampaign.DiscountOfferCode || rawCampaign.discountOfferCode || response.data.discountOfferCode,
            discountOfferDescription: rawCampaign.DiscountOfferDescription || rawCampaign.discountOfferDescription || response.data.discountOfferDescription || '',
            campaignImage: rawCampaign.CampaignImage || rawCampaign.campaignImage || response.data.campaignImage || '',
            campaignImage2: rawCampaign.CampaignImage2 || rawCampaign.campaignImage2 || response.data.campaignImage2 || '', // ✅ Kiosk görseli
            campaignTitle: rawCampaign.CampaignTitle || rawCampaign.campaignTitle || response.data.campaignTitle || '',
            campaignContent: rawCampaign.CampaignContent || rawCampaign.campaignContent || response.data.campaignContent || '',
            campaignContentShort: rawCampaign.CampaignContentShort || rawCampaign.campaignContentShort || response.data.campaignContentShort || '',
            campaignFeatures: rawCampaign.CampaignFeatures || rawCampaign.campaignFeatures || response.data.campaignFeatures || '',
            campaignTermsAndConditions: rawCampaign.CampaignTermsAndConditions || rawCampaign.campaignTermsAndConditions || response.data.campaignTermsAndConditions || '',
            priority: rawCampaign.Priority ?? rawCampaign.priority ?? rawCampaign.campaignPriority ?? response.data.priority ?? response.data.Priority ?? '',
            offices: rawCampaign.Offices || rawCampaign.offices || response.data.offices || '',
            zamanPeriyodu: rawCampaign.ZamanPeriyodu || rawCampaign.zamanPeriyodu || response.data.zamanPeriyodu || '',
            isActiveApp: rawCampaign.IsActiveApp ?? rawCampaign.isActiveApp ?? response.data.isActiveApp ?? 0,
            overwriteStartDate: convertDateFromAPI(rawCampaign.OverwriteStartDate || response.data.overwriteStartDate),
            overwriteEndDate: convertDateFromAPI(rawCampaign.OverwriteEndDate || response.data.overwriteEndDate),
            hideShowEndDate: rawCampaign.HideShowEndDate ?? response.data.hideShowEndDate ?? 0,
            // Görüntüleme için düzenlenmiş alanlar
            title: rawCampaign.CampaignTitle || rawCampaign.campaignTitle || response.data.campaignTitle || rawCampaign.DiscountOfferDescription || rawCampaign.discountOfferDescription || '-',
            image: rawCampaign.CampaignImage || rawCampaign.campaignImage || response.data.campaignImage || '',
            startDate: convertDateFromAPI(rawCampaign.StartDate || rawCampaign.startDate),
            endDate: convertDateFromAPI(rawCampaign.EndDate || rawCampaign.endDate),
            status: (rawCampaign.IsActiveApp ?? rawCampaign.isActiveApp ?? response.data.isActiveApp ?? 0) === 1 ? 'active' : 'inactive',
            participation: rawCampaign.participationRate || response.data.participationRate || 0,
            revenue: rawCampaign.revenue || response.data.revenue || 0,
          };
        } else if (response?.data) {
          // Eğer originalResponse yoksa, data'dan direkt map et
          const data = response.data;
          updatedCampaignData = {
            id: data.attributeCode,
            attributeCode: data.attributeCode,
            discountOfferCode: data.discountOfferCode,
            discountOfferDescription: data.discountOfferDescription || '',
            campaignImage: data.campaignImage || data.CampaignImage || '',
            campaignImage2: data.campaignImage2 || data.CampaignImage2 || '', // ✅ Kiosk görseli
            campaignTitle: data.campaignTitle || '',
            campaignContent: data.campaignContent || '',
            campaignContentShort: data.campaignContentShort || '',
            campaignFeatures: data.campaignFeatures || '',
            campaignTermsAndConditions: data.campaignTermsAndConditions || '',
            priority: data.priority ?? data.Priority ?? data.campaignPriority ?? '',
            offices: data.offices || '',
            zamanPeriyodu: data.zamanPeriyodu || '',
            isActiveApp: data.isActiveApp ?? 0,
            overwriteStartDate: convertDateFromAPI(data.overwriteStartDate),
            overwriteEndDate: convertDateFromAPI(data.overwriteEndDate),
            hideShowEndDate: data.hideShowEndDate ?? 0,
            title: data.campaignTitle || data.discountOfferDescription || '-',
            image: data.campaignImage || data.CampaignImage || '',
            startDate: convertDateFromAPI(data.startDate),
            endDate: convertDateFromAPI(data.endDate),
            status: (data.isActiveApp ?? 0) === 1 ? 'active' : 'inactive',
            participation: data.participationRate || 0,
            revenue: data.revenue || 0,
          };
        }
        
        // State'te kampanyayı bul ve güncelle
        if (updatedCampaignData) {
          const index = state.personalCampaigns.findIndex(campaign => 
            campaign.id === updatedCampaignData.id || 
            campaign.attributeCode === updatedCampaignData.attributeCode
          );
          
          if (index !== -1) {
            // Mevcut kampanyayı güncelle; response priority dönmüyorsa mevcut değeri koru
            state.personalCampaigns[index] = {
              ...state.personalCampaigns[index],
              ...updatedCampaignData,
              priority:
                updatedCampaignData.priority !== '' &&
                updatedCampaignData.priority !== undefined &&
                updatedCampaignData.priority !== null
                  ? updatedCampaignData.priority
                  : state.personalCampaigns[index].priority,
            };
          } else {
            // Eğer kampanya state'te yoksa ekle (yeni kampanya olabilir)
            state.personalCampaigns.push(updatedCampaignData);
          }
        }
      })
      .addCase(updatePersonalCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Referans Çeki Güncelleme
      .addCase(updateReferralCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReferralCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // ✅ PUT response'undan gelen veriyi map et ve state'e ekle
        const response = action.payload;
        let updatedCampaignData = null;
        
        // Response formatı: { success: true, data: { originalResponse: {...}, ... } }
        if (response?.data?.originalResponse) {
          // originalResponse'dan gelen ham veriyi map et
          const rawCampaign = response.data.originalResponse;
          updatedCampaignData = {
            id: rawCampaign.DiscountVoucherTypeCode || response.data.voucherCode,
            voucherCode: rawCampaign.DiscountVoucherTypeCode || response.data.voucherCode,
            description: rawCampaign.DiscountVoucherTypeDescription || response.data.description || '',
            campaignImage: rawCampaign.CampaignImage || response.data.campaignImage || '',
            campaignTitle: rawCampaign.CampaignTitle || response.data.campaignTitle || '',
            campaignContent: rawCampaign.CampaignContent || response.data.campaignContent || '',
            campaignContentShort: rawCampaign.CampaignContentShort || response.data.campaignContentShort || '',
            campaignFeatures: rawCampaign.CampaignFeatures || response.data.campaignFeatures || '',
            campaignTermsAndConditions: rawCampaign.CampaignTermsAndConditions || response.data.campaignTermsAndConditions || '',
            isActiveApp: rawCampaign.IsActiveApp ?? response.data.isActiveApp ?? 0,
            overwriteStartDate: convertDateFromAPI(rawCampaign.OverwriteStartDate),
            overwriteEndDate: convertDateFromAPI(rawCampaign.OverwriteEndDate),
            hideShowEndDate: rawCampaign.HideShowEndDate ?? response.data.hideShowEndDate ?? 0,
            // Görüntüleme için düzenlenmiş alanlar
            title: rawCampaign.CampaignTitle || response.data.campaignTitle || rawCampaign.DiscountVoucherTypeDescription || '-',
            image: rawCampaign.CampaignImage || response.data.campaignImage || '',
            startDate: convertDateFromAPI(rawCampaign.StartDate),
            endDate: convertDateFromAPI(rawCampaign.EndDate),
            status: (rawCampaign.IsActiveApp ?? response.data.isActiveApp ?? 0) === 1 ? 'active' : 'inactive',
            participation: 0,
            revenue: 0,
          };
        } else if (response?.data) {
          // Eğer originalResponse yoksa, data'dan direkt map et
          const data = response.data;
          updatedCampaignData = {
            id: data.voucherCode || data.DiscountVoucherTypeCode,
            voucherCode: data.voucherCode || data.DiscountVoucherTypeCode,
            description: data.description || data.DiscountVoucherTypeDescription || '',
            campaignImage: data.campaignImage || data.CampaignImage || '',
            campaignTitle: data.campaignTitle || data.CampaignTitle || '',
            campaignContent: data.campaignContent || data.CampaignContent || '',
            campaignContentShort: data.campaignContentShort || data.CampaignContentShort || '',
            campaignFeatures: data.campaignFeatures || data.CampaignFeatures || '',
            campaignTermsAndConditions: data.campaignTermsAndConditions || data.CampaignTermsAndConditions || '',
            isActiveApp: data.isActiveApp ?? data.IsActiveApp ?? 0,
            overwriteStartDate: convertDateFromAPI(data.overwriteStartDate || data.OverwriteStartDate),
            overwriteEndDate: convertDateFromAPI(data.overwriteEndDate || data.OverwriteEndDate),
            hideShowEndDate: data.hideShowEndDate ?? data.HideShowEndDate ?? 0,
            title: data.campaignTitle || data.CampaignTitle || data.description || data.DiscountVoucherTypeDescription || '-',
            image: data.campaignImage || data.CampaignImage || '',
            startDate: convertDateFromAPI(data.startDate || data.StartDate),
            endDate: convertDateFromAPI(data.endDate || data.EndDate),
            status: (data.isActiveApp ?? data.IsActiveApp ?? 0) === 1 ? 'active' : 'inactive',
            participation: 0,
            revenue: 0,
          };
        }
        
        // State'te kampanyayı bul ve güncelle
        if (updatedCampaignData) {
          const index = state.referralCampaigns.findIndex(campaign => 
            campaign.id === updatedCampaignData.id || 
            campaign.voucherCode === updatedCampaignData.voucherCode
          );
          
          if (index !== -1) {
            // Mevcut kampanyayı güncelle
            state.referralCampaigns[index] = updatedCampaignData;
          } else {
            // Eğer kampanya state'te yoksa ekle (yeni kampanya olabilir)
            state.referralCampaigns.push(updatedCampaignData);
          }
        }
      })
      .addCase(updateReferralCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Hediye Çeki Güncelleme
      .addCase(updateGiftCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGiftCard.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // ✅ PUT response'undan gelen veriyi map et ve state'e ekle
        const response = action.payload;
        let updatedCampaignData = null;
        
        // Response formatı: { success: true, data: { originalResponse: {...}, ... } }
        if (response?.data?.originalResponse) {
          // originalResponse'dan gelen ham veriyi map et
          const rawCampaign = response.data.originalResponse;
          updatedCampaignData = {
            id: rawCampaign.GiftItemCode || response.data.giftItemCode,
            giftItemCode: rawCampaign.GiftItemCode || response.data.giftItemCode,
            description: rawCampaign.GiftItemDescription || response.data.description || '',
            campaignImage: rawCampaign.CampaignImage || response.data.campaignImage || '',
            campaignTitle: rawCampaign.CampaignTitle || response.data.campaignTitle || '',
            campaignContent: rawCampaign.CampaignContent || response.data.campaignContent || '',
            campaignContentShort: rawCampaign.CampaignContentShort || response.data.campaignContentShort || '',
            campaignFeatures: rawCampaign.CampaignFeatures || response.data.campaignFeatures || '',
            campaignTermsAndConditions: rawCampaign.CampaignTermsAndConditions || response.data.campaignTermsAndConditions || '',
            isActiveApp: rawCampaign.IsActiveApp ?? response.data.isActiveApp ?? 0,
            overwriteStartDate: convertDateFromAPI(rawCampaign.OverwriteStartDate),
            overwriteEndDate: convertDateFromAPI(rawCampaign.OverwriteEndDate),
            hideShowEndDate: rawCampaign.HideShowEndDate ?? response.data.hideShowEndDate ?? 0,
            // Görüntüleme için düzenlenmiş alanlar
            title: rawCampaign.CampaignTitle || response.data.campaignTitle || rawCampaign.GiftItemDescription || '-',
            image: rawCampaign.CampaignImage || response.data.campaignImage || '',
            startDate: convertDateFromAPI(rawCampaign.StartDate),
            endDate: convertDateFromAPI(rawCampaign.EndDate),
            status: (rawCampaign.IsActiveApp ?? response.data.isActiveApp ?? 0) === 1 ? 'active' : 'inactive',
            participation: 0,
            revenue: 0,
          };
        } else if (response?.data) {
          // Eğer originalResponse yoksa, data'dan direkt map et
          const data = response.data;
          updatedCampaignData = {
            id: data.giftItemCode || data.GiftItemCode,
            giftItemCode: data.giftItemCode || data.GiftItemCode,
            description: data.description || data.GiftItemDescription || '',
            campaignImage: data.campaignImage || data.CampaignImage || '',
            campaignTitle: data.campaignTitle || data.CampaignTitle || '',
            campaignContent: data.campaignContent || data.CampaignContent || '',
            campaignContentShort: data.campaignContentShort || data.CampaignContentShort || '',
            campaignFeatures: data.campaignFeatures || data.CampaignFeatures || '',
            campaignTermsAndConditions: data.campaignTermsAndConditions || data.CampaignTermsAndConditions || '',
            isActiveApp: data.isActiveApp ?? data.IsActiveApp ?? 0,
            overwriteStartDate: convertDateFromAPI(data.overwriteStartDate || data.OverwriteStartDate),
            overwriteEndDate: convertDateFromAPI(data.overwriteEndDate || data.OverwriteEndDate),
            hideShowEndDate: data.hideShowEndDate ?? data.HideShowEndDate ?? 0,
            title: data.campaignTitle || data.CampaignTitle || data.description || data.GiftItemDescription || '-',
            image: data.campaignImage || data.CampaignImage || '',
            startDate: convertDateFromAPI(data.startDate || data.StartDate),
            endDate: convertDateFromAPI(data.endDate || data.EndDate),
            status: (data.isActiveApp ?? data.IsActiveApp ?? 0) === 1 ? 'active' : 'inactive',
            participation: 0,
            revenue: 0,
          };
        }
        
        // State'te kampanyayı bul ve güncelle
        if (updatedCampaignData) {
          const index = state.giftCards.findIndex(campaign => 
            campaign.id === updatedCampaignData.id || 
            campaign.giftItemCode === updatedCampaignData.giftItemCode
          );
          
          if (index !== -1) {
            // Mevcut kampanyayı güncelle
            state.giftCards[index] = updatedCampaignData;
          } else {
            // Eğer kampanya state'te yoksa ekle (yeni kampanya olabilir)
            state.giftCards.push(updatedCampaignData);
          }
        }
      })
      .addCase(updateGiftCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Kampanya istatistikleri (tüm kampanyalar)
      .addCase(fetchCampaignsStats.pending, (state) => {
        state.campaignStatsLoading = true;
        state.campaignStatsError = null;
      })
      .addCase(fetchCampaignsStats.fulfilled, (state, action) => {
        state.campaignStatsLoading = false;
        state.campaignStats = action.payload?.data || [];
        state.campaignStatsPagination = action.payload?.pagination || null;
        state.campaignStatsError = null;
      })
      .addCase(fetchCampaignsStats.rejected, (state, action) => {
        state.campaignStatsLoading = false;
        state.campaignStatsError = action.payload;
      })
      
      // Genel Kampanya Güncelleme
      .addCase(updateGeneralCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGeneralCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // ✅ PUT response'undan gelen veriyi map et ve state'e ekle
        const response = action.payload;
        let updatedCampaignData = null;
        
        // Response formatı: { success: true, data: { originalResponse: {...}, ... } }
        if (response?.data?.originalResponse) {
          // originalResponse'dan gelen ham veriyi map et
          const rawCampaign = response.data.originalResponse;
          const rawIsActive = rawCampaign.IsActiveApp ?? rawCampaign.isActiveApp ?? response.data.isActiveApp;
          const mappedIsActive = rawIsActive ?? 0;
          const mappedStatus = mappedIsActive === 1 ? 'active' : 'inactive';
          updatedCampaignData = {
            id: rawCampaign.DiscountOfferCode,
            discountOfferCode: rawCampaign.DiscountOfferCode,
            discountOfferDescription: rawCampaign.DiscountOfferDescription || '',
            campaignImage: rawCampaign.CampaignImage || '',
            campaignImage2: rawCampaign.CampaignImage2 || rawCampaign.campaignImage2 || response.data.campaignImage2 || response.data.CampaignImage2 || '', // ✅ Kiosk küçük banner görseli (her iki formatı da kontrol et)
            campaignImage3: rawCampaign.CampaignImage3 || rawCampaign.campaignImage3 || response.data.campaignImage3 || response.data.CampaignImage3 || '', // ✅ Kiosk büyük banner görseli (her iki formatı da kontrol et)
            campaignTitle: rawCampaign.CampaignTitle || response.data.campaignTitle || '',
            campaignContent: rawCampaign.CampaignContent || response.data.campaignContent || '',
            campaignContentShort: rawCampaign.CampaignContentShort || response.data.campaignContentShort || '',
            campaignFeatures: rawCampaign.CampaignFeatures || response.data.campaignFeatures || '',
            campaignTermsAndConditions: rawCampaign.CampaignTermsAndConditions || response.data.campaignTermsAndConditions || '',
            priority: rawCampaign.Priority ?? rawCampaign.priority ?? rawCampaign.campaignPriority ?? response.data.priority ?? response.data.Priority ?? '',
            offices: rawCampaign.Offices || '',
            isActiveApp: rawCampaign.IsActiveApp ?? response.data.isActiveApp ?? 0,
            overwriteStartDate: convertDateFromAPI(rawCampaign.OverwriteStartDate),
            overwriteEndDate: convertDateFromAPI(rawCampaign.OverwriteEndDate),
            hideShowEndDate: rawCampaign.HideShowEndDate ?? response.data.hideShowEndDate ?? 0,
            // Görüntüleme için düzenlenmiş alanlar
            title: rawCampaign.CampaignTitle || response.data.campaignTitle || rawCampaign.DiscountOfferDescription || '-',
            image: rawCampaign.CampaignImage || '',
            startDate: convertDateFromAPI(rawCampaign.StartDate),
            endDate: convertDateFromAPI(rawCampaign.EndDate),
            status: (rawCampaign.IsActiveApp ?? response.data.isActiveApp ?? 0) === 1 ? 'active' : 'inactive',
            participation: 0,
            revenue: 0,
          };
        } else if (response?.data) {
          // Eğer originalResponse yoksa, data'dan direkt map et
          const data = response.data;
          const dataIsActive = data.isActiveApp ?? data.IsActiveApp ?? 0;
          const dataStatus = dataIsActive === 1 ? 'active' : 'inactive';
          updatedCampaignData = {
            id: data.discountOfferCode,
            discountOfferCode: data.discountOfferCode,
            discountOfferDescription: data.discountOfferDescription || '',
            campaignImage: data.campaignImage || '',
            campaignImage2: data.campaignImage2 || data.CampaignImage2 || '', // ✅ Kiosk küçük banner görseli (her iki formatı da kontrol et)
            campaignImage3: data.campaignImage3 || data.CampaignImage3 || '', // ✅ Kiosk büyük banner görseli (her iki formatı da kontrol et)
            campaignTitle: data.campaignTitle || '',
            campaignContent: data.campaignContent || '',
            campaignContentShort: data.campaignContentShort || '',
            campaignFeatures: data.campaignFeatures || '',
            campaignTermsAndConditions: data.campaignTermsAndConditions || '',
            priority: data.priority ?? data.Priority ?? data.campaignPriority ?? '',
            offices: data.offices || '',
            isActiveApp: dataIsActive,
            overwriteStartDate: convertDateFromAPI(data.overwriteStartDate),
            overwriteEndDate: convertDateFromAPI(data.overwriteEndDate),
            hideShowEndDate: data.hideShowEndDate ?? 0,
            title: data.campaignTitle || data.discountOfferDescription || '-',
            image: data.campaignImage || '',
            startDate: data.startDate || '',
            endDate: data.endDate || '',
            status: dataStatus,
            participation: 0,
            revenue: 0,
          };
        }
        
        // State'te kampanyayı bul ve güncelle
        if (updatedCampaignData) {
        const index = state.generalCampaigns.findIndex(campaign => 
            campaign.id === updatedCampaignData.id || 
            campaign.discountOfferCode === updatedCampaignData.discountOfferCode
        );
          
        if (index !== -1) {
            // Mevcut kampanyayı güncelle; response priority dönmüyorsa mevcut değeri koru
            state.generalCampaigns[index] = {
              ...state.generalCampaigns[index],
              ...updatedCampaignData,
              priority:
                updatedCampaignData.priority !== '' &&
                updatedCampaignData.priority !== undefined &&
                updatedCampaignData.priority !== null
                  ? updatedCampaignData.priority
                  : state.generalCampaigns[index].priority,
            };
          } else {
            // Eğer kampanya state'te yoksa ekle (yeni kampanya olabilir)
            state.generalCampaigns.push(updatedCampaignData);
          }
        }
      })
      .addCase(updateGeneralCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCampaigns, clearPersonalCampaigns, clearReferralCampaigns, clearGiftCards, clearGeneralCampaigns } = campaignSlice.actions;
export default campaignSlice.reducer;
